import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Scale, ThumbsUp, ThumbsDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ObsImage } from './Dashboard';

export default function Challenges() {
  const [tab, setTab] = useState('open');
  const [open, setOpen] = useState([]);
  const [mine, setMine] = useState([]);

  const load = () => {
    api.get('/challenges').then(r => setOpen(r.data)).catch(() => {});
    api.get('/challenges/mine').then(r => setMine(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const vote = async (cid, v) => {
    try {
      const { data } = await api.post(`/challenges/${cid}/vote`, { vote: v });
      if (data.status === 'resolved') {
        toast.success(`Resolved: ${data.resolution === 'challenger' ? 'challenger wins' : data.resolution === 'ai' ? 'AI wins' : 'tie'}${data.xp_delta ? ` (${data.xp_delta > 0 ? '+' : ''}${data.xp_delta} XP)` : ''}`);
      } else {
        toast.success('Vote recorded');
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Vote failed'); }
  };

  return (
    <div className="space-y-5" data-testid="challenges-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Community Verify</p>
        <h2 className="font-serif text-3xl">Challenges</h2>
        <p className="text-sm text-muted-foreground mt-1">Vote on disputed AI identifications. Win rewards the challenger; wrong challenges cost XP.</p>
      </div>

      <div className="flex gap-2">
        <button data-testid="ch-tab-open" onClick={() => setTab('open')} className={`px-4 py-2 rounded-full text-xs ${tab === 'open' ? 'bg-accent text-black font-medium' : 'bg-card border border-white/5 text-muted-foreground'}`}>To Vote ({open.length})</button>
        <button data-testid="ch-tab-mine" onClick={() => setTab('mine')} className={`px-4 py-2 rounded-full text-xs ${tab === 'mine' ? 'bg-accent text-black font-medium' : 'bg-card border border-white/5 text-muted-foreground'}`}>Mine ({mine.length})</button>
      </div>

      {tab === 'open' && (
        open.length === 0 ? (
          <div className="rounded-3xl bg-card border border-white/5 p-8 text-center text-sm text-muted-foreground">No open challenges. Check back later!</div>
        ) : (
          <div className="space-y-4">
            {open.map(c => (
              <div key={c.challenge_id} className="rounded-3xl bg-card border border-white/5 overflow-hidden" data-testid="challenge-card">
                {c.observation?.image_path && (
                  <div className="aspect-video overflow-hidden"><ObsImage path={c.observation.image_path} /></div>
                )}
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-2"><Scale className="w-4 h-4 text-accent" /><span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Which is correct?</span></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl border border-white/10 p-3">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">AI says</p>
                      <p className="font-serif text-base mt-1">{c.original_species_common_name}</p>
                      <p className="text-xs italic text-muted-foreground">{c.original_species_scientific_name}</p>
                    </div>
                    <div className="rounded-2xl border border-accent/40 p-3">
                      <p className="text-[10px] uppercase tracking-widest text-accent">Challenger says</p>
                      <p className="font-serif text-base mt-1">{c.suggested_common_name}</p>
                      <p className="text-xs italic text-muted-foreground">{c.suggested_scientific_name || '—'}</p>
                    </div>
                  </div>
                  {c.note && <p className="text-xs text-muted-foreground italic">"{c.note}"</p>}
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground"><img src={c.challenger?.picture || 'https://i.pravatar.cc/24'} alt="" className="w-5 h-5 rounded-full" /><span>Challenged by {c.challenger?.name || 'Explorer'}</span></div>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <button data-testid={`vote-ai-${c.challenge_id}`} onClick={() => vote(c.challenge_id, 'ai')} className="py-3 rounded-full bg-card border border-white/10 hover:bg-white/5 flex items-center justify-center gap-2 text-sm"><ThumbsUp className="w-4 h-4" /> AI is right</button>
                    <button data-testid={`vote-challenger-${c.challenge_id}`} onClick={() => vote(c.challenge_id, 'challenger')} className="py-3 rounded-full bg-accent text-black font-medium hover:brightness-110 flex items-center justify-center gap-2 text-sm"><ThumbsDown className="w-4 h-4" /> Challenger right</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'mine' && (
        mine.length === 0 ? (
          <div className="rounded-3xl bg-card border border-white/5 p-8 text-center text-sm text-muted-foreground">You haven't challenged any identifications yet.</div>
        ) : (
          <div className="space-y-3">
            {mine.map(c => (
              <div key={c.challenge_id} className={`rounded-2xl bg-card border p-4 ${c.status === 'resolved' && c.resolution === 'challenger' ? 'border-accent/40' : c.status === 'resolved' && c.resolution === 'ai' ? 'border-destructive/40' : 'border-white/5'}`}>
                <p className="text-xs text-muted-foreground line-through">{c.original_species_common_name}</p>
                <p className="font-serif text-lg">{c.suggested_common_name}</p>
                <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-muted-foreground">
                  <span>AI {c.votes_ai} · Challenger {c.votes_challenger}</span>
                  <span className="uppercase tracking-widest text-accent">{c.status}{c.resolution ? ` · ${c.resolution}` : ''}</span>
                  {c.xp_adjustment != null && c.xp_adjustment !== 0 && <span className={c.xp_adjustment > 0 ? 'text-accent' : 'text-destructive'}>{c.xp_adjustment > 0 ? '+' : ''}{c.xp_adjustment} XP</span>}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
